import type {
  NamedResidentDefinition,
  ResidentScheduleEntry,
} from "./types.ts";
import { generateCharacterAppearance } from "../appearance.ts";

const WORKDAY_SCHEDULE: readonly ResidentScheduleEntry[] = [
  { startHour: 0, endHour: 8, anchor: "home", activity: "home" },
  { startHour: 8, endHour: 11.5, anchor: "street", activity: "walking" },
  { startHour: 11.5, endHour: 15, anchor: "work", activity: "working" },
  { startHour: 15, endHour: 19, anchor: "courtyard", activity: "resting" },
  { startHour: 19, endHour: 24, anchor: "home", activity: "home" },
];
const RETIRED_SCHEDULE: readonly ResidentScheduleEntry[] = [
  { startHour: 0, endHour: 8, anchor: "home", activity: "home" },
  { startHour: 8, endHour: 11.5, anchor: "street", activity: "walking" },
  { startHour: 11.5, endHour: 18, anchor: "courtyard", activity: "resting" },
  { startHour: 18, endHour: 24, anchor: "home", activity: "home" },
];
const NIGHT_BAKER_SCHEDULE: readonly ResidentScheduleEntry[] = [
  { startHour: 0, endHour: 7, anchor: "work", activity: "working" },
  { startHour: 7, endHour: 15, anchor: "home", activity: "home" },
  { startHour: 15, endHour: 19, anchor: "street", activity: "walking" },
  { startHour: 19, endHour: 24, anchor: "work", activity: "working" },
];

function resident(
  id: string,
  name: string,
  role: string,
  x: number,
  z: number,
  elderVariant: NamedResidentDefinition["elderVariant"],
  traits: NamedResidentDefinition["traits"],
  coat: number,
  accent: number,
  schedule: readonly ResidentScheduleEntry[] = WORKDAY_SCHEDULE,
): NamedResidentDefinition {
  const elderAppearance = elderVariant === "none"
    ? {}
    : {
        stature: "short" as const,
        hairColour: "silver" as const,
        hairStyle: elderVariant === "plum_scarf"
          ? "cropped" as const
          : "bob" as const,
        headwear: elderVariant === "plum_scarf"
          ? "headscarf" as const
          : "none" as const,
        neckwear: elderVariant === "amber_coat"
          ? "kerchief" as const
          : "none" as const,
      };
  return {
    id,
    name,
    role,
    homeDistrict: "old_town",
    elderVariant,
    traits,
    anchors: {
      home: { x: x - 12, y: 0, z: z + 10 },
      courtyard: { x: x + 18, y: 0, z: z + 16 },
      street: { x, y: 0, z },
      work: { x: x - 22, y: 0, z: z - 18 },
    },
    schedule,
    clothing: { coat, accent },
    appearance: generateCharacterAppearance(id, {
      ...elderAppearance,
      colours: {
        outerwear: coat,
        accent,
      },
    }),
  };
}

/** Final authored M5 cast; these are fictional residents, not real people. */
export const NAMED_RESIDENTS: readonly NamedResidentDefinition[] = [
  resident("ona-vaitkute", "Ona Vaitkutė", "retired archivist", 459, 389, "amber_coat", ["practical", "curious"], 0x76513d, 0xd4a35d, RETIRED_SCHEDULE),
  resident("genovaite-rimkiene", "Genovaitė Rimkienė", "courtyard gardener", 493, 413, "plum_scarf", ["traditional", "practical"], 0x594c55, 0x9a6474, RETIRED_SCHEDULE),
  resident("aldona-jukneviciene", "Aldona Juknevičienė", "former tram dispatcher", 420, 445, "blue_cardigan", ["practical", "traditional"], 0x52636b, 0x8ea4aa, RETIRED_SCHEDULE),
  resident("ruta-kalniene", "Rūta Kalnienė", "bookbinder", 407, 468, "none", ["curious", "practical"], 0x586653, 0xb38b60),
  resident("mantas-simkus", "Mantas Šimkus", "café porter", 438, 432, "none", ["curious"], 0x4a5965, 0xc17a4d),
  resident("antanas-valeika", "Antanas Valeika", "courtyard caretaker", 475, 450, "none", ["traditional", "practical"], 0x625d50, 0x9c8c70),
  resident("ieva-zukauskaite", "Ieva Žukauskaitė", "architecture student", 384, 404, "none", ["curious"], 0x5c5169, 0xb194c1),
  resident("darius-noreika", "Darius Noreika", "bicycle mechanic", 522, 388, "none", ["practical"], 0x435c58, 0xb1774f),
  resident("laima-petrauskiene", "Laima Petrauskienė", "choir alto", 535, 444, "none", ["traditional", "curious"], 0x6b4e52, 0xc49b78),
  resident("tomas-bartkus", "Tomas Bartkus", "museum guard", 365, 455, "none", ["practical", "traditional"], 0x4b5359, 0xa79672),
  resident("emilija-urbaite", "Emilija Urbaitė", "florist", 505, 354, "none", ["curious", "practical"], 0x667052, 0xd2a06b),
  resident("saulius-kazlauskas", "Saulius Kazlauskas", "night baker", 395, 365, "none", ["practical"], 0x5f4b42, 0xbda278, NIGHT_BAKER_SCHEDULE),
] as const;
